import { Injectable, Injector } from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { CookieService } from 'ngx-cookie';
import { HttpClientCustom } from '../custom/http-client';
import { filter } from 'rxjs/operators';
import type { Auth } from '../auth/auth';

export interface UtmParams {
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_term?: string;
  utm_content?: string;
  date_added_to_cookie?: string;
}

@Injectable({
  providedIn: 'root'
})
export class UtmService {
  private readonly UTM_COOKIE_NAME = '__utmztidy';
  private readonly UTM_COOKIE_EXPIRY_DAYS = 90; // Store UTMs for 90 days

  constructor(
    private cookieService: CookieService,
    private httpClient: HttpClientCustom,
    private injector: Injector,
    private router: Router
  ) {
    // Listen to route changes to capture UTMs when already logged in
    this.router.events
      .pipe(filter(event => event instanceof NavigationEnd))
      .subscribe(() => {
        if (this.getAuth().isAuthenticated()) {
          this.captureUtmsFromUrl();
        }
      });
  }

  /**
   * Lazily resolves Auth to avoid circular dependency
   */
  private getAuth(): Auth {
    // Lazy import to avoid circular dependency (Auth -> ... -> UtmService -> Auth)
    const { Auth } = require('../auth/auth');
    return this.injector.get(Auth);
  }

  /**
   * Captures UTM parameters from the current URL
   * @returns UtmParams object with all found UTM parameters
   */
  captureUtmsFromUrl(): UtmParams {
    const utmParams: UtmParams = {};

    try {
      const urlParams = new URLSearchParams(window.location.search);
      const utmKeys = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content'];

      utmKeys.forEach(key => {
        const value = urlParams.get(key);
        if (value) {
          utmParams[key] = value;
        }
      });

      // If we found any UTM parameters, save them
      if (Object.keys(utmParams).length > 0) {
        this.saveUtmsToCookie(utmParams);

        // If user is authenticated, also save to backend
        if (this.getAuth().isAuthenticated()) {
          this.saveUtmsToBackend(utmParams);
        }
      }
    } catch (error) {
      console.error('Error capturing UTMs from URL:', error);
    }

    return utmParams;
  }

  /**
   * Gets UTM parameters from the cookie
   * Handles migration from old format ({ source, dateTime }) to new format ({ utm_source, date_added_to_cookie })
   * @returns UtmParams object from cookie or empty object
   */
  getUtmsFromCookie(): UtmParams {
    try {
      const cookieValue = this.cookieService.get(this.UTM_COOKIE_NAME);
      if (cookieValue) {
        const parsed = JSON.parse(cookieValue);

        // Handle old format migration: { source, dateTime } -> { utm_source, date_added_to_cookie }
        if (parsed.source && !parsed.utm_source) {
          return {
            utm_source: parsed.source,
            date_added_to_cookie: parsed.dateTime
          };
        }

        return parsed;
      }
    } catch (error) {
      console.error('Error reading UTM cookie:', error);
    }
    return {};
  }

  /**
   * Saves UTM parameters to cookie
   * @param utmParams UTM parameters to save
   */
  saveUtmsToCookie(utmParams: UtmParams): void {
    try {
      const existingUtms = this.getUtmsFromCookie();
      const dateTime = new Date().toISOString();

      // Merge with existing UTMs, but prioritize new ones if they exist
      const mergedUtms: UtmParams = {
        ...existingUtms,
        ...utmParams,
        date_added_to_cookie: existingUtms.date_added_to_cookie || dateTime
      };

      // Only update date_added_to_cookie if this is the first time we're seeing UTMs
      if (!existingUtms.date_added_to_cookie && Object.keys(utmParams).length > 0) {
        mergedUtms.date_added_to_cookie = dateTime;
      }

      // Save to cookie with expiry
      const expiryDate = new Date();
      expiryDate.setDate(expiryDate.getDate() + this.UTM_COOKIE_EXPIRY_DAYS);

      this.cookieService.put(
        this.UTM_COOKIE_NAME,
        JSON.stringify(mergedUtms),
        {
          expires: expiryDate,
          path: '/'
        }
      );
    } catch (error) {
      console.error('Error saving UTMs to cookie:', error);
    }
  }

  /**
   * Saves UTM parameters to backend using the new tracking endpoints
   * @param utmParams UTM parameters to save
   */
  async saveUtmsToBackend(utmParams: UtmParams): Promise<void> {
    try {
      if (!this.getAuth().isAuthenticated()) {
        return;
      }

      const endpoint = 'api/v1/customer/utm-trackings';

      // Format payload according to new backend requirements
      const utmTrackings = Object.keys(utmParams)
        .filter(key => key.startsWith('utm_') && utmParams[key])
        .map(key => ({
          key,
          value: utmParams[key]
        }));

      if (utmTrackings.length === 0) {
        return;
      }

      const payload = {
        utm_trackings: utmTrackings,
        tracked_at: utmParams.date_added_to_cookie || new Date().toISOString()
      };

      await this.httpClient.post(endpoint, payload);
    } catch (error) {
      // Silently fail - UTM saving shouldn't break the user experience
      console.error('Error saving UTMs to backend:', error);
    }
  }

  /**
   * Gets UTM parameters formatted for login/signup credentials
   * @returns Object with UTM parameters for authentication
   */
  getUtmsForAuth(): { utm_source?: string; utm_medium?: string; utm_campaign?: string; utm_term?: string; utm_content?: string; date_added_to_cookie?: string } {
    const utmCookie = this.getUtmsFromCookie();

    return {
      utm_source: utmCookie.utm_source || '',
      utm_medium: utmCookie.utm_medium || '',
      utm_campaign: utmCookie.utm_campaign || '',
      utm_term: utmCookie.utm_term || '',
      utm_content: utmCookie.utm_content || '',
      date_added_to_cookie: utmCookie.date_added_to_cookie || ''
    };
  }

  /**
   * Initializes UTM capture on app startup
   * Should be called in app.component.ts ngOnInit
   */
  initialize(): void {
    // Capture UTMs from initial URL load
    this.captureUtmsFromUrl();
  }
}
